import * as React from "react";
import type { AttachmentRef, KnowledgeBase } from "@lupp/shared";
import { fetchKnowledgeBases } from "../services/knowledge";

export interface SourcePickerProps {
  onAdd: (ref: AttachmentRef) => void;
  onClose: () => void;
  /** Welche Ansicht direkt geöffnet wird (vom Composer je nach Menüpunkt gesetzt). */
  initialView?: View;
}

type View = "knowledge" | "website";

export default function SourcePicker({ onAdd, onClose, initialView = "knowledge" }: SourcePickerProps): JSX.Element {
  // "Wissen anhängen" und "Webseite anhängen" sind getrennte Einstiege — die Ansicht
  // ist fix (kein Umschalten innerhalb des Pickers mehr).
  const [view] = React.useState<View>(initialView);
  const [url, setUrl] = React.useState("");

  // Knowledge-base picker state
  const [kbs, setKbs] = React.useState<KnowledgeBase[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [query, setQuery] = React.useState("");
  const [selected, setSelected] = React.useState<Set<string>>(new Set());

  React.useEffect(() => {
    if (view !== "knowledge") return undefined;
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchKnowledgeBases()
      .then((list) => {
        if (!cancelled) setKbs(list);
      })
      .catch((err: Error) => {
        if (!cancelled) setError(err.message || "Konnte Wissensquellen nicht laden.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [view]);

  function submitUrl(): void {
    const trimmed = url.trim();
    if (!trimmed) return;
    onAdd({ type: "text", url: trimmed });
    setUrl("");
    onClose();
  }

  function toggle(id: string): void {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function confirmKnowledge(): void {
    for (const kb of kbs) {
      if (selected.has(kb.id)) onAdd({ type: "collection", id: kb.id, name: kb.name });
    }
    onClose();
  }

  if (view === "website") {
    return (
      <div className="source-picker" role="dialog" aria-label="Webseite anhängen">
        <div className="source-picker__header">
          <span>Webseite anhängen</span>
          <button className="icon-btn" type="button" aria-label="Schließen" onClick={onClose}>
            ✕
          </button>
        </div>
        <div className="source-picker__body">
          <input
            className="source-picker__url-input"
            type="url"
            placeholder="https://..."
            value={url}
            onChange={(e): void => setUrl(e.target.value)}
            onKeyDown={(e): void => {
              if (e.key === "Enter") submitUrl();
            }}
            autoFocus
          />
          <button className="source-picker__add-btn" type="button" aria-label="Anhängen" onClick={submitUrl}>
            Anhängen
          </button>
        </div>
      </div>
    );
  }

  // Default view: knowledge bases (opens directly, no intermediate menu).
  const filtered = kbs.filter(
    (kb) =>
      kb.name.toLowerCase().includes(query.toLowerCase()) ||
      (kb.description ?? "").toLowerCase().includes(query.toLowerCase()),
  );
  return (
      <div className="source-picker source-picker--knowledge" role="dialog" aria-label="Wissensquellen">
        <div className="source-picker__header">
          <span>Wissensquellen</span>
          <button className="icon-btn" type="button" aria-label="Schließen" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="source-picker__search-row">
          <input
            className="source-picker__search"
            type="text"
            placeholder="Wissensquellen suchen …"
            value={query}
            onChange={(e): void => setQuery(e.target.value)}
            autoFocus
          />
        </div>

        <div className="source-picker__kb-list">
          {loading && <div className="source-picker__empty">Lädt …</div>}
          {error && <div className="source-picker__empty source-picker__empty--error">{error}</div>}
          {!loading && !error && filtered.length === 0 && (
            <div className="source-picker__empty">Keine Wissensquellen gefunden.</div>
          )}
          {!loading &&
            !error &&
            filtered.map((kb) => {
              const isSel = selected.has(kb.id);
              return (
                <button
                  key={kb.id}
                  type="button"
                  className={`source-picker__kb-item${isSel ? " source-picker__kb-item--selected" : ""}`}
                  onClick={(): void => toggle(kb.id)}
                  aria-pressed={isSel}
                >
                  <span className="source-picker__kb-check" aria-hidden="true">
                    {isSel ? "✓" : ""}
                  </span>
                  <span className="source-picker__kb-body">
                    <span className="source-picker__kb-name">{kb.name}</span>
                    {kb.description && <span className="source-picker__kb-desc">{kb.description}</span>}
                  </span>
                </button>
              );
            })}
        </div>

        <div className="source-picker__footer">
          <button
            className="source-picker__add-btn"
            type="button"
            disabled={selected.size === 0}
            onClick={confirmKnowledge}
          >
            Wissensquelle hinzufügen{selected.size > 0 ? ` (${selected.size})` : ""}
          </button>
        </div>
      </div>
    );
}
